import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigationRefs } from '../../api/agent/navigation';

/**
 * Hands the router to the agent's navigation module. Renders nothing.
 *
 * `api/agent/navigation.js` runs outside React — from room events, timers and
 * the dev console — so it cannot call `useNavigate()` itself. This component
 * copies the current `navigate` and `location` into `navigationRefs` after
 * every commit.
 *
 * Refs, not context or effect dependencies: in react-router v6 `navigate`
 * changes identity whenever the location does, and anything that listed it as a
 * dependency (the provider's connect effect, say) would re-run on every page
 * the user opens.
 *
 * Written in a layout effect rather than during render so the location the
 * agent waits on is one React actually committed. A render that is thrown away
 * never reaches the refs, and the write still lands before the browser paints
 * and before any page's passive effects run.
 *
 * Must be mounted inside the router and inside the copilot provider.
 */
export default function AgentNavigationBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const written = useRef(null);

  /* Declared first so that on unmount (and StrictMode's simulated remount) the
     clear runs before the writer below re-installs — and only clears what this
     instance wrote, never a newer bridge's values. */
  useLayoutEffect(() => () => {
    if (written.current && navigationRefs.navigate === written.current.navigate) {
      navigationRefs.navigate = null;
      navigationRefs.location = null;
    }
    written.current = null;
  }, []);

  useLayoutEffect(() => {
    navigationRefs.navigate = navigate;
    navigationRefs.location = location;
    written.current = { navigate, location };
    /* An agent navigate waiting to see its URL is told of every commit, so a
       location replaced before its next poll still counts as landed. */
    [...navigationRefs.commits].forEach((onCommit) => onCommit(location));
  });

  return null;
}

export { AgentNavigationBridge };
